/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Popconfirm, Tooltip, Row, Select, Modal } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
  FileAddOutlined,
  FileDoneOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import fileDownload from 'js-file-download';

import styles from '../styles';
import {
  getJobcharts,
  clearJobcharts,
  clearJobchartFiles,
  getJobchartAcknowledgements,
  clearJobchartAcknowledgements,
} from '../Actions';
import {
  errorNotification,
  errorNotificationOnBlob,
  successNotification,
} from '../Snackbar';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  get,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Api from '../Api';
import constants from '../constants';
import Breadcrumbs from '../Components/Breadcrumbs';
import { currentFinancialYear, financialYears } from '../financial-years';

const { Option } = Select;

const JobchartList = props => {
  const { classes } = props;

  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);

  const jobcharts = useSelector(state => state.jobcharts);
  const jobchartAcknowledgements = useSelector(
    state => state.jobchartAcknowledgements
  );

  const [Type, SetType] = useState('running');
  const [Year, SetYear] = useState(currentFinancialYear);

  useEffect(() => {
    return () => {
      dispatch(clearJobcharts());
      dispatch(clearJobchartFiles());
    };
  }, []);

  const onDelete = async id => {
    try {
      await Api.delete(`/jobcharts/${id}`);
      successNotification('Jobchart deleted');
      dispatch(getJobcharts());
    } catch (err) {
      errorNotification(err);
    }
  };

  const onShip = async id => {
    try {
      await Api.put(`/jobcharts/ship/${id}`);
      successNotification('Jobchart shipped');
      dispatch(getJobcharts());
    } catch (err) {
      errorNotification(err);
    }
  };

  const onFileDownload = async id => {
    try {
      const { data } = await Api.getFile(`/jobcharts/download/${id}`);
      fileDownload(data, `jobchart-${id}.pdf`);
      successNotification('File downloaded');
    } catch (err) {
      errorNotificationOnBlob(err);
    }
  };

  useEffect(() => {
    const conditions = { year: Year };
    if (Type === 'shipped') {
      conditions.isShipped = true;
    } else {
      conditions.isShipped = false;
    }

    dispatch(getJobcharts(conditions));

    return () => {
      dispatch(clearJobcharts());
      dispatch(clearJobchartFiles());
    };
  }, [Type, Year]);

  const preivewColumn = record => (
    <Tooltip placement="bottom" title="View">
      <Link to={`/jobcharts/preview/${record.id}`} target="_blank">
        <FileOutlined className={classes.tableIcon} />
      </Link>
    </Tooltip>
  );

  const acknowledgeColumn = (_text, record) => (
    <span
      style={{
        color: record.acknowledgement === 'Acknowledged' ? 'Blue' : 'red',
      }}
      onClick={() => {
        record.acknowledgement = 'Acknowledged';
        dispatch(getJobchartAcknowledgements(record.id));
      }}
    >
      {record.acknowledgement}
    </span>
  );

  const trackingColumn = record => (
    <Tooltip placement="bottom" title="Tracking">
      <Link to={`/jobcharts/tracking/${record.id}`} target="_blank">
        <FileOutlined className={classes.tableIcon} />
      </Link>
    </Tooltip>
  );

  const downloadColumn = record => (
    <Tooltip placement="bottom" title="Download">
      <DownloadOutlined
        className={classes.tableIcon}
        onClick={() => onFileDownload(record.id)}
      />
    </Tooltip>
  );

  const deleteColumn = record => (
    <>
      {permissions.includes(constants.permissions.deleteJobchart) ? (
        <Tooltip placement="bottom" title="Delete">
          <Popconfirm
            title="Are you sure you want to delete?"
            onConfirm={() => onDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <DeleteOutlined className={classes.tableIcon} />
          </Popconfirm>
        </Tooltip>
      ) : null}
    </>
  );

  const shipColumn = record => (
    <Tooltip placement="bottom" title="Ship">
      <Popconfirm
        title="Are you sure job chart is shipped?"
        onConfirm={() => onShip(record.id)}
        okText="Yes"
        cancelText="No"
      >
        <FileDoneOutlined className={classes.tableIcon} />
      </Popconfirm>
    </Tooltip>
  );

  const timeAction = (timeActionId, record) => {
    if (timeActionId) {
      return (
        <Tooltip placement="bottom" title="T&A View">
          <Link
            to={`/timeactions/${record.id}`}
            className={classes.defaultLink}
            target="_blank"
          >
            <FileOutlined className={classes.tableIcon} />
          </Link>
        </Tooltip>
      );
    }

    if (
      !timeActionId &&
      permissions.includes(constants.permissions.timeAction)
    ) {
      return (
        <Tooltip placement="bottom" title="Create T&A">
          <Link
            to={`/timeactions/create/${record.id}`}
            className={classes.defaultLink}
          >
            <FileAddOutlined className={classes.tableIcon} />
          </Link>
        </Tooltip>
      );
    }
    return null;
  };

  const columns = useMemo(() => {
    const _columns = [
      {
        title: 'Customer',
        dataIndex: 'customer',
        sorter: sorter('customer'),
        onFilter: onFilter('customer'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'BPO #',
        dataIndex: 'bpoNo',
        sorter: sorter('bpoNo'),
        onFilter: onFilter('bpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Products',
        dataIndex: 'products',
        sorter: sorter('products'),
        onFilter: onFilter('products'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Ship Date',
        dataIndex: 'shipDate',
        sorter: dateSorter('shipDate'),
        onFilter: onDateFilter('shipDate'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: shipDate =>
          shipDate ? moment(shipDate).format('DD MMM YYYY') : null,
      },
      {
        title: 'BPO Date',
        dataIndex: 'jobDate',
        sorter: dateSorter('jobDate'),
        onFilter: onDateFilter('jobDate'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: jobDate =>
          jobDate ? moment(jobDate).format('DD MMM YYYY') : null,
      },
      {
        title: 'Job Date',
        dataIndex: 'createdAt',
        sorter: dateSorter('createdAt'),
        onFilter: onDateFilter('createdAt'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: createdAt =>
          createdAt ? moment(createdAt).format('DD MMM YYYY') : null,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        sorter: sorter('status'),
        onFilter: onFilter('status'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Time & Action',
        dataIndex: 'timeActionId',
        render: timeAction,
      },
      {
        title: 'T&A Status',
        dataIndex: 'tnaStatus',
        sorter: sorter('tnaStatus'),
        onFilter: onFilter('tnaStatus'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Acknowledge',
        dataIndex: 'acknowledgement',
        sorter: sorter('acknowledgement'),
        onFilter: (value, record) =>
          record.acknowledgement.indexOf(value) === 0,
        filterIcon,
        filters: [
          {
            text: 'Acknowledge Now',
            value: 'Acknowledge Now',
          },
          {
            text: 'Acknowledged',
            value: 'Acknowledged',
          },
        ],
        render: acknowledgeColumn,
      },
    ];

    if (jobcharts[0] && jobcharts[0].isShipped) {
      _columns.push(
        {
          title: 'Shipped By',
          dataIndex: 'shippedBy',
          sorter: sorter('shippedBy'),
          onFilter: onFilter('shippedBy'),
          filterDropdown,
          filterIcon,
        },
        {
          title: 'ShippedAt',
          dataIndex: 'shippedAt',
          sorter: dateSorter('shippedAt'),
          onFilter: onDateFilter('shippedAt'),
          filterDropdown: dateFilterDropdown,
          filterIcon,
          render: shippedAt =>
            shippedAt ? moment(shippedAt).format('DD MMM YYYY') : null,
        }
      );
    } else {
      _columns.push({
        title: 'Ship Jobchart',
        render: shipColumn,
      });
    }

    _columns.push({
      title: 'Version',
      dataIndex: 'versions',
      sorter: sorter('versions'),
      onFilter: onFilter('versions'),
      filterDropdown,
      filterIcon,
    });

    if (permissions.includes(constants.permissions.deleteJobchart)) {
      _columns.push({
        title: 'Delete',
        render: deleteColumn,
      });
    }

    _columns.push(
      {
        title: 'Created',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            sorter: dateSorter('createdAt'),
            onFilter: onDateFilter('createdAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: createdAt =>
              createdAt ? moment(createdAt).format('DD MMM YYYY') : null,
          },
        ],
      },
      {
        title: 'Last Updated',
        children: [
          {
            title: 'By',
            dataIndex: 'updatedBy',
            sorter: sorter('updatedBy'),
            onFilter: onFilter('updatedBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'updatedAt',
            sorter: dateSorter('updatedAt'),
            onFilter: onDateFilter('updatedAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: updatedAt =>
              updatedAt ? moment(updatedAt).format('DD MMM YYYY') : null,
          },
        ],
      },
      {
        title: 'View',
        render: preivewColumn,
        width: '3%',
        fixed: 'right',
      },
      {
        title: 'Tracking',
        render: trackingColumn,
        width: '4%',
        fixed: 'right',
      },
      {
        title: 'Download',
        render: downloadColumn,
        width: '4%',
        fixed: 'right',
      }
    );

    return _columns;
  }, [jobcharts[0]]);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = {
    defaultPageSize: 20,
    position: ['bottomLeft'],
    showTotal: total => `Total ${total} Records`,
  };

  const scroll = useMemo(() => ({ x: 2900, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Jobchart"
        moduleLink="/jobcharts/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Row>
          <Select
            defaultValue="running"
            style={{ width: 150 }}
            onChange={value => SetType(value)}
          >
            <Option key="running" value="running">
              Running
            </Option>
            <Option key="shipped" value="shipped">
              Shipped
            </Option>
          </Select>
          &nbsp;
          <Select
            value={Year}
            style={{ width: 150 }}
            onChange={value => SetYear(value)}
          >
            <Select.Option value="All">All</Select.Option>
            {financialYears.map(el => (
              <Select.Option key={el} value={el}>
                {el}
              </Select.Option>
            ))}
          </Select>
        </Row>
        <br />
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={jobcharts}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
      {jobchartAcknowledgements.length ? (
        <Modal
          centered
          visible
          width={600}
          closable={false}
          cancelButtonProps={{ style: { display: 'none' } }}
          onOk={() => dispatch(clearJobchartAcknowledgements())}
        >
          <Table
            bordered
            size="small"
            dataSource={jobchartAcknowledgements}
            columns={[
              { title: 'User', dataIndex: 'user' },
              {
                title: 'Date & Time',
                dataIndex: 'createdAt',
                render: jobDate =>
                  jobDate ? moment(jobDate).format('DD MMM YYYY') : null,
              },
            ]}
          />
        </Modal>
      ) : null}
    </>
  );
};

JobchartList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(JobchartList);
